import { createSelector } from 'reselect';
import createFetchSelectors from './createFetchSelectors';
import get from '../helpers/get';
import makeItemLink from '../helpers/makeItemLink';
import createPerSliceIdSelector from '../helpers/createPerSliceIdSelector';
import { getSliceContent } from './content';

export const getIconicStoriesSliceIds = state => Object.keys(
  get(state, ['stories', 'slices']) || [],
);

export const getIconicStoriesSliceFilterInfoById = (state, sliceId) => (
  get(state, ['stories', 'slices', sliceId, 'filters']) || {}
);

export const getIconicStoriesCopyHidden = createPerSliceIdSelector(
  getSliceContent,
  content => get(content, ['hide_copy']) === 'Yes',
);

export const getIconicStoriesGridSize = createPerSliceIdSelector(
  getSliceContent,
  content => (get(content, ['grid_size']) === '6 Columns' ? 6 : 4),
);

export const getIconicStoriesSliceFilterIds = createPerSliceIdSelector(
  getIconicStoriesSliceFilterInfoById,
  filterInfoById => Object.keys(filterInfoById).map(filterId => Number(filterId)),
);

export const getIconicStoriesSliceFilterNames = createPerSliceIdSelector(
  getIconicStoriesSliceFilterInfoById,
  filterInfoById => Object.values(filterInfoById).map(({ name }) => name),
);

export const iconicStoriesSliceHasMultipleFilters = (state, sliceId) => (
  getIconicStoriesSliceFilterIds(state, sliceId).length > 1
);

export const getIconicStoriesSliceFilterId = (state, sliceId) => (
  get(state, ['stories', 'slices', sliceId, 'filterId'])
);

export const getIconicStoriesSliceFilterName = createPerSliceIdSelector(
  getIconicStoriesSliceFilterInfoById,
  getIconicStoriesSliceFilterId,
  (filterInfoById, filterId) => get(filterInfoById, [filterId, 'name']),
);

export const getIconicStoriesSliceFilterClickthrough = createPerSliceIdSelector(
  getIconicStoriesSliceFilterInfoById,
  getIconicStoriesSliceFilterId,
  (filterInfoById, filterId) => get(filterInfoById, [filterId, 'clickthrough']),
);

export const getActiveIconicStoriesFilterIds = createSelector(
  state => getIconicStoriesSliceIds(state).reduce((filterIdsMap, sliceId) => {
    const filterId = getIconicStoriesSliceFilterId(state, sliceId);
    if (filterId) {
      return {
        ...filterIdsMap,
        [filterId]: true,
      };
    }
    return filterIdsMap;
  }, {}),
  activeFilterIdsMap => Object.keys(activeFilterIdsMap).map(filterId => Number(filterId)),
);

const {
  getPageSize,
  getPage,
  getItems,
  isBusy,
  getError,
  isReadyOrError,
} = createFetchSelectors({
  stateSliceName: 'stories',
  itemTypePlural: 'tiles',
  itemSetNamePlural: 'filters',
  defaultPageSize: 25,
});

// the above selectors accept (state, filterId) so translate them to accept (state, sliceId)
export const getIconicStoriesPageSize = (state, sliceId) => getPageSize(
  state,
  getIconicStoriesSliceFilterId(state, sliceId),
);

export const getIconicStoriesPage = (state, sliceId) => getPage(
  state,
  getIconicStoriesSliceFilterId(state, sliceId),
);

export const getIconicStories = createPerSliceIdSelector(
  (state, sliceId) => getItems(state, getIconicStoriesSliceFilterId(state, sliceId)),
  getIconicStoriesSliceFilterClickthrough,
  (tiles, clickthrough) => tiles.map(tile => ({
    ...tile,
    link: tile.id ? makeItemLink(tile, clickthrough, 'id') : undefined,
  })),
);

export const isIconicStoriesBusy = (state, sliceId) => isBusy(
  state,
  getIconicStoriesSliceFilterId(state, sliceId),
);

export const getIconicStoriesError = (state, sliceId) => getError(
  state,
  getIconicStoriesSliceFilterId(state, sliceId),
);

export const isIconicStoriesReadyOrError = (state, sliceId) => isReadyOrError(
  state,
  getIconicStoriesSliceFilterId(state, sliceId),
);
